/** Build help payloads grouped by assessment pillar/section. */

export function buildHelpQuestion(q) {
  return {
    id: q?.id != null ? String(q.id) : "",
    text: q?.text || "",
    terms: (q?.terms || []).map((t) => ({
      phrase: t.phrase || "",
      definition: t.definition || "",
    })),
  };
}

export function buildHelpQuestions(questions) {
  return (questions || [])
    .map(buildHelpQuestion)
    .filter((q) => q.text || q.terms.length);
}

export function buildHelpPillar({ step, title, questions }) {
  const normalizedStep =
    step != null && step !== "" ? Number(step) : undefined;
  return {
    step: Number.isFinite(normalizedStep) ? normalizedStep : step,
    title: title || (step != null ? `Section ${step}` : "Assessment"),
    questions: buildHelpQuestions(questions),
  };
}

export function buildHelpContext({
  step,
  sectionTitle,
  pillars = [],
  questions,
  assessmentTrack,
  focusedPillarStep,
  focusedQuestionId,
  lastAnsweredQuestionId,
  firstUnansweredQuestionId,
  answeredCount,
  idleSeconds,
  totalSteps,
  productName,
  typeKey,
  isFiveX,
}) {
  let pillarList = (pillars || []).filter(
    (p) => p && (p.questions?.length || p.title)
  );

  if (!pillarList.length && questions?.length) {
    pillarList = [
      buildHelpPillar({
        step,
        title: sectionTitle,
        questions,
      }),
    ];
  }

  const focusedKey =
    focusedPillarStep != null ? Number(focusedPillarStep) : null;
  const focused =
    pillarList.find((p) => Number(p.step) === focusedKey) ||
    pillarList.find((p) => Number(p.step) === Number(step)) ||
    pillarList[0];

  const focusedQid =
    focusedQuestionId != null && focusedQuestionId !== ""
      ? String(focusedQuestionId)
      : "";
  const lastAnsweredQid =
    lastAnsweredQuestionId != null && lastAnsweredQuestionId !== ""
      ? String(lastAnsweredQuestionId)
      : "";
  const firstUnansweredQid =
    firstUnansweredQuestionId != null && firstUnansweredQuestionId !== ""
      ? String(firstUnansweredQuestionId)
      : "";

  const fiveX =
    typeof isFiveX === "boolean"
      ? isFiveX
      : !typeKey || String(typeKey).toLowerCase() === "5x_v1";

  return {
    step: focused?.step ?? step,
    section_title: focused?.title || sectionTitle || "Assessment",
    focused_pillar_step: focused?.step ?? focusedPillarStep ?? step,
    focused_question_id: focusedQid,
    last_answered_question_id: lastAnsweredQid,
    first_unanswered_question_id: firstUnansweredQid,
    pillars: pillarList,
    assessment_track: assessmentTrack,
    product_name: productName || undefined,
    type_key: typeKey || undefined,
    is_five_x: fiveX,
    answered_count:
      answeredCount != null && Number.isFinite(Number(answeredCount))
        ? Number(answeredCount)
        : undefined,
    idle_seconds:
      idleSeconds != null && Number.isFinite(Number(idleSeconds))
        ? Number(idleSeconds)
        : undefined,
    total_steps:
      totalSteps != null && Number.isFinite(Number(totalSteps))
        ? Number(totalSteps)
        : undefined,
  };
}

/** Resolve a help question by id across pillars. */
export function findHelpQuestionById(pillars, questionId) {
  const target =
    questionId != null && questionId !== "" ? String(questionId) : "";
  if (!target) return null;
  for (const pillar of pillars || []) {
    for (const q of pillar.questions || []) {
      if (String(q.id) === target) return q;
    }
  }
  return null;
}

/** Question for nudges: current screen first, then first unanswered. */
export function findNudgeHelpQuestion(pillars, { firstUnansweredQuestionId, focusedQuestionId }) {
  return (
    findHelpQuestionById(pillars, focusedQuestionId) ||
    findHelpQuestionById(pillars, firstUnansweredQuestionId) ||
    pillars?.[0]?.questions?.[0] ||
    null
  );
}

function questionNudgeLabel(question) {
  if (!question) return "";
  const qid = question.id ? String(question.id) : "";
  let text = (question.text || "").trim();
  if (text.length > 48) text = `${text.slice(0, 45).trim()}…`;
  if (qid && text) return `question ${qid} (${text})`;
  if (qid) return `question ${qid}`;
  if (text) return `this statement (${text})`;
  return "this question";
}

/** Client fallback when the nudge API is unavailable. */
/** Keep suggestion chips scoped to the question on screen. */
export function filterHelpChipsForQuestion(chips, focusedQuestionId) {
  const focused =
    focusedQuestionId != null && focusedQuestionId !== ""
      ? String(focusedQuestionId)
      : "";
  return (chips || []).filter((c) => {
    if (c.kind === "next_question") return false;
    const qid = c.question_id != null ? String(c.question_id) : "";
    if (qid && focused && qid !== focused) return false;
    const q = String(c.question || "");
    const explainMatch = q.match(/^Explain question ([^\s:]+)/i);
    if (explainMatch && focused && explainMatch[1] !== focused) return false;
    return true;
  });
}

export function helpChipLabel(chip) {
  if (chip?.label) return chip.label;
  if (chip?.kind === "current") return "Explain this question";
  const q = String(chip?.question || "");
  if (q.startsWith("What is ")) return q.replace(/^What is /, "").replace(/\?$/, "");
  if (/^Explain question/i.test(q)) return "Explain more";
  return q;
}

export function buildHelpNudgeFallback({
  pillars,
  focusedQuestionId,
  firstUnansweredQuestionId,
  sectionTitle,
}) {
  const focused = findNudgeHelpQuestion(pillars, {
    firstUnansweredQuestionId,
    focusedQuestionId,
  });
  if (focused) {
    const qid = focused.id ? String(focused.id) : "";
    const text = String(focused.text || "").trim();
    const suggested = qid && text
      ? `Explain question ${qid}: ${text}`
      : qid
        ? `Explain question ${qid}`
        : "";
    return {
      message: `Stuck on ${questionNudgeLabel(focused)}? I can walk you through it.`,
      suggested_question: suggested,
    };
  }
  const section = sectionTitle || "this section";
  return {
    message: `You've been on ${section} for a while. Need help with this section?`,
    suggested_question: "",
  };
}
